/**
 * Custom hook for managing selection handlers in DrivePanel
 *
 * Extracted from DrivePanel.tsx
 */

import { useCallback, useMemo } from 'react';
import type { TableItem } from '../../../services/driveService';
import type { MinimalDocument } from '../../../contexts/DocumentSelectionContext';
import type {
  UseDriveSelectionHandlersProps,
  UseDriveSelectionHandlersReturn,
} from '../types/drive-panel.types';

/**
 * Manages selection handlers and computed selection states
 */
export function useDriveSelectionHandlers({
  tableItems,
  selectedDocumentsFromContext,
  internalSelectedDocuments,
  setInternalSelectedDocuments,
  selectContextDocuments,
  deselectContextDocuments,
  setContextDocument,
  createMinimalDocumentFromTableItem,
  handleDocumentToggle,
  currentUser,
}: UseDriveSelectionHandlersProps): UseDriveSelectionHandlersReturn {
  // Computed selection states
  const selectedItems = useMemo(
    () => tableItems.filter(item => internalSelectedDocuments.includes(item.id)),
    [tableItems, internalSelectedDocuments]
  );

  const deletableItems = useMemo(
    () => {
      if (currentUser?.is_admin) {
        return selectedItems;
      }
      return selectedItems.filter(item => item.type !== 'document' || item.origin === 'private');
    },
    [selectedItems, currentUser]
  );

  const reprocessableItems = useMemo(
    () => selectedItems.filter(item => item.type === 'document'),
    [selectedItems]
  );

  // Usar el contexto como fuente de verdad para determinar el estado del checkbox
  // IMPORTANTE: Solo comparar contra los IDs que están en tableItems (documentos visibles en la tabla)
  // Optimizado con useMemo para evitar recálculos innecesarios
  const checkboxState = useMemo(() => {
    const selectedTableItemsCount = tableItems.filter(item =>
      selectedDocumentsFromContext.includes(item.id)
    ).length;
    const allSelected = tableItems.length > 0 && selectedTableItemsCount === tableItems.length;
    const someSelected = selectedTableItemsCount > 0 && selectedTableItemsCount < tableItems.length;

    return { selectedTableItemsCount, allSelected, someSelected };
  }, [tableItems, selectedDocumentsFromContext]);

  // Toggle individual document - usar el handler del hook selection que maneja los refs correctamente
  const handleDocumentToggleWrapper = useCallback(
    (documentId: string) => {
      // Antes de toggle, asegurar que el documento existe en el contexto
      const item = tableItems.find(i => i.id === documentId);
      if (item) {
        const minimalDoc = createMinimalDocumentFromTableItem(item);
        if (minimalDoc) {
          setContextDocument(minimalDoc);
        }
      }

      // Usar el handleDocumentToggle del hook useDriveSelection que maneja correctamente los refs
      handleDocumentToggle(documentId);
    },
    [tableItems, createMinimalDocumentFromTableItem, setContextDocument, handleDocumentToggle]
  );

  // handleDocumentToggle y handleSelectAll están en el hook useDriveSelection
  const handleSelectAllItems = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Usar el valor del evento (checked) para determinar la acción
      // Esto es más confiable porque refleja el estado del checkbox en el momento del clic
      const shouldSelectAll = event.target.checked;

      if (shouldSelectAll) {
        // Seleccionar todos los items visibles
        const allIds = tableItems.map(item => item.id);

        // Crear MinimalDocuments desde TableItems y registrarlos en el contexto ANTES de seleccionarlos
        // Esto evita intentar cargar snippets/studios/mensajes como documentos desde la API
        tableItems.forEach(item => {
          const minimalDoc = createMinimalDocumentFromTableItem(item);
          if (minimalDoc) {
            // Registrar en el contexto inmediatamente (síncrono)
            setContextDocument(minimalDoc);
          }
        });

        // Los MinimalDocuments ya están registrados síncronamente, así que podemos seleccionar inmediatamente
        // No necesitamos queueMicrotask ya que setContextDocument es síncrono
        selectContextDocuments(allIds, 'manual');
      } else {
        // Deseleccionar solo los items visibles en la tabla (Drive), no todos los del contexto
        // Esto evita deseleccionar items de otros paneles (Saved Content, etc.)
        const tableItemIds = tableItems.map(item => item.id);
        const selectedTableItemIds = selectedDocumentsFromContext.filter(id => tableItemIds.includes(id));
        if (selectedTableItemIds.length > 0) {
          deselectContextDocuments(selectedTableItemIds);
        }
      }
    },
    [
      tableItems,
      selectContextDocuments,
      deselectContextDocuments,
      selectedDocumentsFromContext,
      createMinimalDocumentFromTableItem,
      setContextDocument,
    ]
  );

  return {
    handleDocumentToggleWrapper,
    handleSelectAllItems,
    selectedItems,
    deletableItems,
    reprocessableItems,
    checkboxState,
  };
}
